import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import type { ConfigPlanner } from '@miolos/core';
import { construirDocumento, construirPaleta } from '@miolos/core';
import { DocumentoImpressao } from '@/impressao/DocumentoImpressao';
import { preCarregarImagem, urlsDeImagem } from '@/impressao/useImpressao';
import { BASE, urlDaImagem } from '@/lib/api';

/**
 * Página de impressão avulsa — o que o gerador de PDF do servidor abre.
 *
 * Ela monta o miolo inteiro na área de impressão, espera imagens e fontes e só
 * então levanta a bandeira `__mioloPronto`. O servidor espera por essa bandeira
 * antes de chamar o `page.pdf()`, senão o arquivo sairia com páginas em branco.
 *
 * Quando **não há** o que desenhar (passe vencido, miolo apagado, link
 * incompleto), ela levanta `__mioloErro` com o motivo: o servidor para de esperar
 * na mesma hora e a falha chega explicada a quem pediu o PDF, em vez de virar
 * três minutos de espera.
 *
 * O acesso é por um token de uso único que o servidor assina na hora: esta rota é
 * pública (quem abre é o próprio Chromium do servidor, sem sessão), mas só serve
 * o miolo para quem tem o token — válido por poucos minutos, para um miolo só.
 *
 * Nada aqui aparece para o usuário: em tela a área de impressão é `display: none`
 * (o CSS do site), e o `page.pdf()` do Chromium usa a mídia de impressão.
 */
type JanelaDaImpressao = {
  __mioloPronto?: boolean;
  __mioloPaginas?: number;
  /** Motivo quando não há o que desenhar — o servidor para de esperar por isso. */
  __mioloErro?: string;
};

/** Marca na janela que a página não tem como desenhar o miolo. */
function avisarFalha(motivo: string) {
  (window as unknown as JanelaDaImpressao).__mioloErro = motivo;
}

export function Impressao() {
  const { id } = useParams<{ id: string }>();
  const [parametros] = useSearchParams();
  const token = parametros.get('t') ?? '';

  const [miolo, setMiolo] = useState<{ nome: string; config: ConfigPlanner } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  /** O erro da tela é o mesmo recado que o servidor recebe. */
  useEffect(() => {
    if (erro) avisarFalha(erro);
  }, [erro]);

  useEffect(() => {
    if (!id || !token) {
      setErro('Link de impressão incompleto.');
      return;
    }

    let cancelado = false;
    fetch(`${BASE}/api/impressao/${encodeURIComponent(id)}?t=${encodeURIComponent(token)}`)
      .then(async (resposta) => {
        if (!resposta.ok) throw new Error('O link de impressão expirou.');
        return (await resposta.json()) as { nome: string; config: ConfigPlanner };
      })
      .then((dados) => {
        if (!cancelado) setMiolo(dados);
      })
      .catch((falha: Error) => {
        if (!cancelado) setErro(falha.message);
      });

    return () => {
      cancelado = true;
    };
  }, [id, token]);

  const documento = useMemo(
    () => (miolo ? construirDocumento(miolo.config) : null),
    [miolo],
  );
  const paleta = useMemo(() => (miolo ? construirPaleta(miolo.config.cores) : null), [miolo]);

  /**
   * Avisa o servidor quando o documento está pronto para virar PDF.
   *
   * A ordem importa: pré-carregar as imagens (a capa enviada, os cadernos de
   * desenho), esperar as fontes e só então dois quadros de animação — o segundo
   * garante que o navegador já pintou o documento inteiro.
   */
  useEffect(() => {
    if (!documento) return;
    let cancelado = false;

    const preparar = async () => {
      const janela = window as unknown as JanelaDaImpressao;
      janela.__mioloPronto = false;
      janela.__mioloPaginas = documento.paginas.length;

      await Promise.all(urlsDeImagem(documento).map((url) => preCarregarImagem(urlDaImagem(url))));

      if (typeof document !== 'undefined' && 'fonts' in document) {
        await document.fonts.ready.catch(() => undefined);
      }

      await new Promise<void>((resolver) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolver()));
      });

      if (!cancelado) janela.__mioloPronto = true;
    };

    void preparar();

    return () => {
      cancelado = true;
    };
  }, [documento]);

  if (erro) {
    return (
      <p className="p-8 text-sm text-muted-foreground" role="alert">
        {erro}
      </p>
    );
  }

  if (!documento || !paleta || !miolo) return null;

  /*
   * A área de impressão sai num portal para o `body`.
   *
   * Não é estilo: em mídia de impressão o site esconde tudo que não seja filho
   * direto do `body` — a área montada dentro do `#root` seria escondida junto com
   * o resto da interface, e o PDF sairia em branco. É o mesmo caminho que a
   * impressão normal usa.
   */
  return createPortal(
    <div className="area-impressao">
      <DocumentoImpressao
        documento={documento}
        paleta={paleta}
        config={miolo.config}
        resolverImagem={urlDaImagem}
      />
    </div>,
    document.body,
  );
}
